// AI-CONTEXT: See docs/features/update-aanvragen.md

import updateRequestStatusLabel, { statusNoteIsQuestion } from '../../helpers/updateRequests/updateRequestStatus'
import updateRequestSeverityLabel, { isUpdateRequestSeverity } from '../../helpers/updateRequests/updateRequestSeverity'
import formatRequestDate from '../../helpers/updateRequests/formatRequestDate'
import { UPDATE_MODULES } from '../../helpers/updates/updateModules'
import findUpdateById from '../../helpers/updates/findUpdateById'
import formatUpdateDate from '../../helpers/updates/formatUpdateDate'
import updates from '../../data/updates.json'

/**
 * @typedef {object} UpdateRequest
 * @property {string} ID - Uniek id van het verzoek
 * @property {string} Title - Korte omschrijving
 * @property {string} Body - Toelichting van de aanvrager
 * @property {string} [Module] - Categorie uit updateModules.js
 * @property {string} [Severity] - Hoe erg het voor de aanvrager is; ontbreekt bij verzoeken van vóór dat veld
 * @property {string} Status - nieuw | in behandeling | meer uitleg nodig | gebouwd | afgerond | afgewezen
 * @property {string} [StatusNote] - Antwoord van Deccos, of de vraag bij 'meer uitleg nodig'
 * @property {string} [UpdateID] - De update uit updates.json die dit verzoek oplevert
 * @property {string} [SubmittedByName] - Naam van de aanvrager
 * @property {{toDate?: () => Date}} [Timestamp] - Wanneer het is ingediend
 */

/**
 * De weergave van een lijst updateverzoeken.
 *
 * Bewust alleen weergave: waar de verzoeken vandaan komen verschilt per plek
 * (eigen omgeving op de Updates-pagina, klant-omgeving op de projectdetail-
 * pagina) en dat hoort niet hier. Eén component in plaats van twee kopieën —
 * anders lopen ze binnen een paar weken uiteen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} props
 * @param {Array<UpdateRequest>} props.requests - Al gesorteerde verzoeken
 */
const UpdateRequestList = ({ requests }) => (
  <div className="update-request-list">
    {requests.map(request => {
      const datum = formatRequestDate(request.Timestamp)
      const categorie = UPDATE_MODULES[request.Module]
      // Een onbekend id geeft null en dus geen regel; het verzoek zelf blijft staan.
      const update = findUpdateById(updates, request.UpdateID)
      // 'gebouwd' = de update komt nog; daarna is hij doorgevoerd.
      const updateVoorzetsel = request.Status === 'gebouwd' ? 'Komt mee in de update' : 'Doorgevoerd in de update'
      // Bij 'meer uitleg nodig' is de toelichting een vraag aan deze organisatie
      // en geen mededeling. Zonder eigen kop leest hij als 'even ter info' en
      // blijft het verzoek liggen omdat niemand doorheeft dat hij aan zet is.
      const vraag = statusNoteIsQuestion(request.Status) && Boolean(request.StatusNote)
      // Geen label bij een verzoek van vóór dit veld: 'Niet opgegeven' erbij
      // zetten maakt van een ontbrekend veld een mededeling, en dat is het niet.
      const ernst = isUpdateRequestSeverity(request.Severity) ? request.Severity : ''

      return (
        <article className="update-request-item" key={request.ID}>
          <div className="update-request-item-date">{datum}</div>
          <div className="update-request-item-content">
            <h3>{request.Title}</h3>
            <p className="update-request-item-body">{request.Body}</p>

            {vraag &&
              <div className="update-request-item-note update-request-item-note--question" role="note" aria-label="Vraag van Deccos">
                <h4>Vraag van Deccos</h4>
                <p>{request.StatusNote}</p>
              </div>
            }

            {!vraag && request.StatusNote &&
              <p className="update-request-item-note">{request.StatusNote}</p>
            }

            {update &&
              <p className="update-request-item-update">
                {updateVoorzetsel} <b>{update.title}</b>
                {formatUpdateDate(update.date) ? ` van ${formatUpdateDate(update.date)}` : ''}
              </p>
            }

            <div className="update-request-item-meta">
              {/* De status als klasse-achtervoegsel: 'in behandeling' bevat een
                  spatie, en die maakt er stilzwijgend twee klassen van. */}
              <span className={`update-request-status update-request-status--${String(request.Status || '').replace(/\s+/g, '-')}`}>
                {updateRequestStatusLabel(request.Status)}
              </span>

              {ernst &&
                <span className={`update-request-severity update-request-severity--${ernst}`}>
                  {updateRequestSeverityLabel(ernst)}
                </span>
              }

              {categorie &&
                <span className="update-item-module">{categorie.label}</span>
              }

              {request.SubmittedByName &&
                <span className="update-request-item-by">{request.SubmittedByName}</span>
              }
            </div>
          </div>
        </article>
      )
    })}
  </div>
)

export default UpdateRequestList
